import { baseApi } from 'api/baseApi';
import { fieldsOfTypeLoaded } from 'slices/field.slice';
import type { TAppThunk, TRootState } from 'slices/store';
import { z } from 'zod';
import type { TField } from '@timeedit/types';

// The API caps limit at 1000; a type with more fields than that would need paging
const MAX_FIELDS_PER_TYPE = 1000;
const LOOKUP_TIMEOUT_MS = 15_000;

type TFieldFindResponse = { results: TField[] };

// The envelope only; the fields inside are trusted to match TField
const fieldFindResponseSchema = z.object({
  results: z.array(z.looseObject({})),
});

const isFieldFindResponse = (value: unknown): value is TFieldFindResponse =>
  fieldFindResponseSchema.safeParse(value).success;

export const fieldTypeTag = (typeId: number) => ({
  type: 'Field' as const,
  id: `TYPE-${typeId}`,
});

export const fieldsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // A type's own and inherited fields in global field order, ~150 ms against seconds for
    // the full field list. Sorting is only honoured with typeId, not with ids.
    getFieldsOfType: build.query<TField[], number>({
      query: (typeId) => ({
        url: '/fields/find',
        method: 'POST',
        body: { typeId, sort: 'FIELD_ORDER', limit: MAX_FIELDS_PER_TYPE },
        timeout: LOOKUP_TIMEOUT_MS,
      }),
      transformResponse: (raw: unknown) => {
        if (!isFieldFindResponse(raw)) {
          throw new Error('Unexpected response from /fields/find');
        }
        return raw.results;
      },
      providesTags: (_result, _error, typeId) => [fieldTypeTag(typeId)],
      // Existing readers take fields from the fields slice; keep it fed
      onQueryStarted: async (typeId, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(fieldsOfTypeLoaded({ typeId, fields: data }));
        } catch {
          // The query state carries the error; nothing to store
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const { useGetFieldsOfTypeQuery } = fieldsApi;

export type TFieldsStatus = 'loading' | 'ready' | 'failed';

const fieldReadFailed = (state: TRootState, typeId: number) =>
  fieldsApi.endpoints.getFieldsOfType.select(typeId)(state).isError;

// Whether the fields of every given type are here; 'failed' as soon as one read has failed
export const selectFieldsStatus = (
  state: TRootState,
  typeIds: readonly number[]
): TFieldsStatus => {
  if (typeIds.every((typeId) => state.fields.orderByType[typeId] != null)) {
    return 'ready';
  }
  return typeIds.some((typeId) => fieldReadFailed(state, typeId))
    ? 'failed'
    : 'loading';
};

// Reads the fields again of the types whose read failed
export const retryFieldsOfTypes =
  (typeIds: readonly number[]): TAppThunk =>
  (dispatch, getState) => {
    typeIds
      .filter((typeId) => fieldReadFailed(getState(), typeId))
      .forEach((typeId) =>
        dispatch(
          fieldsApi.endpoints.getFieldsOfType.initiate(typeId, {
            subscribe: false,
            forceRefetch: true,
          })
        )
      );
  };
